import "@/App.css";
import "@/i18n";
import { useEffect, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { GuidedTourBar } from "@/components/demo/GuidedTourBar";
import { BusinessProvider } from "@/contexts/BusinessContext";
import { captureRefFromUrl, initResellerPixel } from "@/lib/referral";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { lazyRetry as lazy, startVersionWatch, useStaleReload } from "@/lib/lazyRetry";
import { fbTrack } from "@/lib/metaPixel";
import { isCustomHost } from "@/lib/customHost";
import { OAuthPopupBridge } from "@/components/ConnectionHealthBanner";

let _mpFirstPageView = true;

if (localStorage.getItem('veluro_theme') === 'light') {
  document.documentElement.classList.remove('dark');
}
import { AuthCallback } from "@/components/AuthCallback";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminProtectedRoute } from "@/components/AdminProtectedRoute";
import { BETA_MODE } from "@/lib/launchMode";
import { isCustomDomainHost } from "@/lib/host";
import HomePage from "@/pages/home/HomePage";
import { HomeRoute, VelaRoute, OnboardingRoute, VelaSetupRoute } from "@/components/vela/VelaGate";

// Code-split everything below the fold — keeps the landing bundle small & fast on mobile
const FeaturesPage = lazy(() => import("@/pages/FeaturesPage"));
const HowItWorksPage = lazy(() => import("@/pages/HowItWorksPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const UpgradePage = lazy(() => import("@/pages/UpgradePage"));
const TrialStartedPage = lazy(() => import("@/pages/TrialStartedPage"));
const FixPaymentPage = lazy(() => import("@/pages/FixPaymentPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const JoinTeamPage = lazy(() => import("@/pages/JoinTeamPage"));
const PublicLandingPage = lazy(() => import("@/pages/PublicLandingPage"));
const VanteraDemoPage = lazy(() => import("@/pages/VanteraDemoPage"));
const FlyersPage = lazy(() => import("@/pages/FlyersPage"));
const DecksPage = lazy(() => import("@/pages/DecksPage"));
const ProposalsPage = lazy(() => import("@/pages/ProposalsPage"));
const ProposalSignPage = lazy(() => import("@/pages/ProposalSignPage"));
const StoryboardPage = lazy(() => import("@/pages/StoryboardPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const CampaignsPage = lazy(() => import("@/pages/CampaignsPage"));
const CampaignWorkspacePage = lazy(() => import("@/pages/CampaignWorkspacePage"));
const GrowthPage = lazy(() => import("@/pages/GrowthPage"));
const LeadsPage = lazy(() => import("@/pages/LeadsPage"));
const DemandMapPage = lazy(() => import("@/pages/DemandMapPage"));
const SchedulingPage = lazy(() => import("@/pages/SchedulingPage"));
const SalesCoachPage = lazy(() => import("@/pages/SalesCoachPage"));
const TeamPage = lazy(() => import("@/pages/TeamPage"));
const DigestPage = lazy(() => import("@/pages/DigestPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const HelpPage = lazy(() => import("@/pages/HelpPage"));
const HealthScorePage = lazy(() => import("@/pages/HealthScorePage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const ManageBookingPage = lazy(() => import("@/pages/ManageBookingPage"));
const AdminLoginPage = lazy(() => import("@/pages/admin/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("@/pages/admin/AdminDashboardPage"));
const CashClubSignupPage = lazy(() => import("@/pages/cashclub/CashClubSignupPage"));
const CashClubAdminPage = lazy(() => import("@/pages/cashclub/CashClubAdminPage"));
const CashClubSnapshotPage = lazy(() => import("@/pages/cashclub/CashClubSnapshotPage"));
const SocialHomePage = lazy(() => import("@/pages/SocialHomePage"));
const CompetitorsPage = lazy(() => import("@/pages/CompetitorsPage"));
const CreativeInsightsPage = lazy(() => import("@/pages/CreativeInsightsPage"));
const EmailPage = lazy(() => import("@/pages/EmailPage"));
const OutreachPage = lazy(() => import("@/pages/OutreachPage"));
const CreativeStudioPage = lazy(() => import("@/pages/CreativeStudioPage"));
const FunnelsPage = lazy(() => import("@/pages/FunnelsPage"));
const FunnelEditorPage = lazy(() => import("@/pages/FunnelEditorPage"));
const FunnelCanvasPage = lazy(() => import("@/pages/FunnelCanvasPage"));
const PublicFunnelPage = lazy(() => import("@/pages/PublicFunnelPage"));
const SharedDemandMapPage = lazy(() => import("@/pages/SharedDemandMapPage"));
const VanityRedirect = lazy(() => import("@/pages/VanityRedirect"));
const LandingPagesPage = lazy(() => import("@/pages/LandingPagesPage"));
const AdBudgetPlannerPage = lazy(() => import("@/pages/AdBudgetPlannerPage"));
const ChatWidgetsPage = lazy(() => import("@/pages/ChatWidgetsPage"));
const ConversationAgentPage = lazy(() => import("@/pages/ConversationAgentPage"));
const PixelsPage = lazy(() => import("@/pages/PixelsPage"));
const LinkedInPage = lazy(() => import("@/pages/LinkedInPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const DemoPage = lazy(() => import("@/pages/DemoPage"));
const NurtureHubPage = lazy(() => import("@/pages/NurtureHubPage"));
const VideoStudioPage = lazy(() => import("@/pages/VideoStudioPage"));
const DirectorPage = lazy(() => import("@/pages/DirectorPage"));
const RepliesInboxPage = lazy(() => import("@/pages/RepliesInboxPage"));
const IntentPage = lazy(() => import("@/pages/IntentPage"));
const ConciergePage = lazy(() => import("@/pages/ConciergePage"));
const TeachVeluroPage = lazy(() => import("@/pages/TeachVeluroPage"));
const TryItPage = lazy(() => import("@/pages/TryItPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const SeoPage = lazy(() => import("@/pages/SeoPage"));
const LocalListingsPage = lazy(() => import("@/pages/LocalListingsPage"));
const GeoEnginePage = lazy(() => import("@/pages/GeoEnginePage"));
const PublicBlogPage = lazy(() => import("@/pages/PublicBlogPage"));
const ReplyDrafterPage = lazy(() => import("@/pages/ReplyDrafterPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const PublicPolicyPage = lazy(() => import("@/pages/PublicPolicyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const DataDeletionPage = lazy(() => import("@/pages/DataDeletionPage"));
const PartnerLandingPage = lazy(() => import("@/pages/partners/PartnerLandingPage"));
const PartnerEntry = lazy(() => import("@/pages/partners/PartnerEntry"));
const PartnerDashboardPage = lazy(() => import("@/pages/PartnerDashboardPage"));
const PartnerJoinPage = lazy(() => import("@/pages/PartnerJoinPage"));
const MarketingMaterialsPage = lazy(() => import("@/pages/MarketingMaterialsPage"));
const ServiceBrainPage = lazy(() => import("@/pages/ServiceBrainPage"));
const ComparePage = lazy(() => import("@/pages/ComparePage"));
const CompareCompetitorPage = lazy(() => import("@/pages/CompareCompetitorPage"));

const CustomDomainApp = lazy(() => import("@/pages/CustomDomainApp"));

const RouteFallback = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center">
    <div className="h-8 w-8 rounded-full border-2 border-violet-500/30 border-t-violet-500 animate-spin" />
  </div>
);

const AppRouter = () => {
  const location = useLocation();
  useStaleReload(location.pathname);

  // Capture ?ref= referral codes anywhere on the site (90-day attribution)
  useEffect(() => {
    captureRefFromUrl();
    initResellerPixel();
  }, [location.search]);

  // Meta Pixel: fire a PageView on every SPA route change (the first PageView already fires from index.html)
  useEffect(() => {
    if (_mpFirstPageView) { _mpFirstPageView = false; return; }
    fbTrack('PageView');
  }, [location.pathname]);

  // A customer's own domain (Cloudflare for SaaS → veluro-pages Worker) serves exactly one published funnel.
  if (isCustomHost()) {
    return <Suspense fallback={<RouteFallback />}><CustomDomainApp /></Suspense>;
  }

  // Check URL fragment for session_id synchronously (prevents race conditions)
  if (location.hash?.includes('session_id=') || location.pathname === '/auth/google') {
    return <AuthCallback />;
  }

  // ── BETA MODE: routes stay mounted (so admin can reach /login + dashboard directly).
  //    Public-facing auth CTAs are hidden on the landing page itself via BETA_MODE flag
  //    inside LandingPage.js — they swap to the BetaSignupModal automatically.
  //    To fully lock the app down, also gate /login below.
  return (
    <Suspense fallback={<RouteFallback />}>
    <OAuthPopupBridge />
    <Routes>
      <Route path="/" element={isCustomDomainHost() ? <PublicLandingPage byHost /> : <HomePage />} />
      <Route path="/platform" element={<Navigate to="/" replace />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/compare" element={<ComparePage />} />
      <Route path="/compare/:competitor" element={<CompareCompetitorPage />} />
      <Route path="/sign/:token" element={<ProposalSignPage />} />
      <Route path="/flyers" element={<ProtectedRoute><FlyersPage /></ProtectedRoute>} />
      <Route path="/decks" element={<ProtectedRoute><DecksPage /></ProtectedRoute>} />
      <Route path="/decks/:deckId" element={<ProtectedRoute><DecksPage /></ProtectedRoute>} />
      <Route path="/proposals" element={<ProtectedRoute><ProposalsPage /></ProtectedRoute>} />
      <Route path="/demo/:slug" element={<DemoPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/policy/:slug" element={<PublicPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/join-team/:token" element={<JoinTeamPage />} />
      <Route path="/signup" element={<LoginPage defaultRegister />} />
      <Route path="/vanterademo" element={<VanteraDemoPage />} />
      <Route path="/aspirepartnerspro" element={<PartnerLandingPage type="aspire_pro" />} />
      <Route path="/partners" element={<PartnerLandingPage type="partners" />} />
      <Route path="/resellpartners" element={<PartnerLandingPage type="resell" />} />
      <Route path="/arc" element={<PartnerLandingPage type="arc" />} />
      <Route path="/bluecoast" element={<PartnerLandingPage type="bluecoast" />} />
      <Route path="/propartner" element={<PartnerLandingPage type="propartner" />} />
      <Route path="/platinumpartners" element={<PartnerLandingPage type="platinum" />} />
      <Route path="/diamondpartners" element={<PartnerLandingPage type="diamond" />} />
      <Route path="/veluropartner" element={<PartnerLandingPage type="veluro" />} />
      <Route path="/partner" element={<PartnerEntry />} />
      <Route path="/partner/join/:token" element={<PartnerJoinPage />} />
      <Route path="/try" element={<TryItPage />} />
      <Route path="/b/:brandSlug" element={<PublicBlogPage />} />
      <Route path="/b/:brandSlug/:postSlug" element={<PublicBlogPage />} />
      <Route path="/p/:slug" element={<PublicLandingPage />} />
      <Route path="/book/:slug" element={<BookingPage />} />
      <Route path="/booking/manage/:appointmentId" element={<ManageBookingPage />} />
      {/* Admin Portal — completely separate from main app */}
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboardPage /></AdminProtectedRoute>} />
      {/* Cash Club white-label reseller — separate signup + admin portal */}
      <Route path="/cashclub" element={<CashClubSignupPage />} />
      <Route path="/cashclubadmin" element={<CashClubAdminPage />} />
      <Route path="/snapshot/:token" element={<CashClubSnapshotPage />} />
      <Route path="/dm/:token" element={<SharedDemandMapPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingRoute /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/home" element={<ProtectedRoute><HomeRoute /></ProtectedRoute>} />
      <Route path="/vela" element={<Navigate to="/home" replace />} />
      <Route path="/vela/setup" element={<ProtectedRoute><VelaSetupRoute /></ProtectedRoute>} />
      <Route path="/vela/:tab" element={<ProtectedRoute><VelaRoute /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ConciergePage /></ProtectedRoute>} />
      <Route path="/teach-veluro" element={<ProtectedRoute><TeachVeluroPage /></ProtectedRoute>} />
      <Route path="/service-brain" element={<ProtectedRoute><ServiceBrainPage /></ProtectedRoute>} />
      <Route path="/reply-drafter" element={<ProtectedRoute><ReplyDrafterPage /></ProtectedRoute>} />
      <Route path="/health" element={<ProtectedRoute><HealthScorePage /></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
      <Route path="/campaigns/workspace/:campaignId?" element={<ProtectedRoute><CampaignWorkspacePage /></ProtectedRoute>} />
      <Route path="/growth" element={<ProtectedRoute><GrowthPage /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
      <Route path="/demand-map" element={<ProtectedRoute><DemandMapPage /></ProtectedRoute>} />
      <Route path="/social" element={<ProtectedRoute><SocialHomePage /></ProtectedRoute>} />
      <Route path="/social/legacy" element={<Navigate to="/social" replace />} />
      <Route path="/social/classic" element={<Navigate to="/social?compose=1" replace />} />
      <Route path="/ads" element={<Navigate to="/campaigns" replace />} />
      <Route path="/reports" element={<Navigate to="/dashboard?tab=reports" replace />} />
      <Route path="/competitors" element={<ProtectedRoute><CompetitorsPage /></ProtectedRoute>} />
      <Route path="/creative-insights" element={<ProtectedRoute><CreativeInsightsPage /></ProtectedRoute>} />
      <Route path="/email" element={<ProtectedRoute><EmailPage /></ProtectedRoute>} />
      <Route path="/outreach" element={<ProtectedRoute><OutreachPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<Navigate to="/dashboard?tab=analytics" replace />} />
      <Route path="/studio" element={<ProtectedRoute blockFreePartner><CreativeStudioPage /></ProtectedRoute>} />
      <Route path="/studio/storyboard" element={<ProtectedRoute blockFreePartner><StoryboardPage /></ProtectedRoute>} />
      <Route path="/marketing-materials" element={<ProtectedRoute><MarketingMaterialsPage /></ProtectedRoute>} />
      <Route path="/funnels" element={<ProtectedRoute><FunnelsPage /></ProtectedRoute>} />
      <Route path="/funnels/:funnelId/editor/:pageId" element={<ProtectedRoute><FunnelEditorPage /></ProtectedRoute>} />
      <Route path="/funnels/:funnelId/canvas/:pageId" element={<ProtectedRoute><FunnelCanvasPage /></ProtectedRoute>} />
      <Route path="/f/draft/:funnelId" element={<ProtectedRoute><PublicFunnelPage /></ProtectedRoute>} />
      <Route path="/f/draft/:funnelId/:pagePath" element={<ProtectedRoute><PublicFunnelPage /></ProtectedRoute>} />
      <Route path="/f/preview/:token" element={<PublicFunnelPage />} />
      <Route path="/f/preview/:token/:pagePath" element={<PublicFunnelPage />} />
      <Route path="/f/:slug" element={<PublicFunnelPage />} />
      <Route path="/f/:slug/:pagePath" element={<PublicFunnelPage />} />
      <Route path="/landing-pages" element={<ProtectedRoute><LandingPagesPage /></ProtectedRoute>} />
      <Route path="/ads/budget-planner" element={<ProtectedRoute><AdBudgetPlannerPage /></ProtectedRoute>} />
      <Route path="/chat-widgets" element={<ProtectedRoute><ChatWidgetsPage /></ProtectedRoute>} />
      <Route path="/conversation-agent" element={<ProtectedRoute><ConversationAgentPage /></ProtectedRoute>} />
      <Route path="/pixels" element={<ProtectedRoute><PixelsPage /></ProtectedRoute>} />
      <Route path="/linkedin" element={<ProtectedRoute><LinkedInPage /></ProtectedRoute>} />
      <Route path="/linkedin-messaging" element={<Navigate to="/linkedin?tab=compose" replace />} />
      <Route path="/nurture" element={<ProtectedRoute><NurtureHubPage /></ProtectedRoute>} />
      <Route path="/video-studio" element={<ProtectedRoute><VideoStudioPage /></ProtectedRoute>} />
      <Route path="/creative-studio" element={<ProtectedRoute><VideoStudioPage /></ProtectedRoute>} />
      <Route path="/creative-studio/p/:projectId" element={<ProtectedRoute><VideoStudioPage /></ProtectedRoute>} />
      <Route path="/director" element={<ProtectedRoute><DirectorPage /></ProtectedRoute>} />
      <Route path="/director/:projectId" element={<ProtectedRoute><DirectorPage /></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><RepliesInboxPage /></ProtectedRoute>} />
      <Route path="/unified-analytics" element={<Navigate to="/dashboard?tab=command-center" replace />} />
      <Route path="/intent" element={<ProtectedRoute><IntentPage /></ProtectedRoute>} />
      <Route path="/blog" element={<ProtectedRoute><BlogPage /></ProtectedRoute>} />
      <Route path="/seo" element={<ProtectedRoute><SeoPage /></ProtectedRoute>} />
      <Route path="/local-listings" element={<ProtectedRoute><LocalListingsPage /></ProtectedRoute>} />
      <Route path="/geo" element={<ProtectedRoute><GeoEnginePage /></ProtectedRoute>} />
      <Route path="/scheduling" element={<ProtectedRoute><SchedulingPage /></ProtectedRoute>} />
      <Route path="/sales-coach" element={<ProtectedRoute><SalesCoachPage /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/digest" element={<ProtectedRoute><DigestPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/upgrade" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
      <Route path="/billing/trial-started" element={<ProtectedRoute><TrialStartedPage /></ProtectedRoute>} />
      <Route path="/billing/update-card" element={<ProtectedRoute><FixPaymentPage /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
      <Route path="/:vanity" element={<VanityRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
};

// Light theme is ONLY for signed-in app pages — every public/marketing/partner page is locked to dark.
const PUBLIC_DARK_PREFIXES = ['/pricing', '/demo', '/sign/', '/privacy', '/terms', '/data-deletion', '/login',
  '/signup', '/reset-password', '/aspirepartnerspro', '/partners', '/resellpartners', '/arc', '/bluecoast', '/propartner',
  '/platinumpartners', '/diamondpartners', '/veluropartner', '/partner/join', '/try', '/b/', '/p/', '/book/',
  '/booking/', '/admin', '/onboarding', '/auth', '/platform', '/features', '/how-it-works', '/cashclub', '/cashclubadmin', '/snapshot/', '/dm/', '/compare'];
function ThemeEnforcer() {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname;
    const isPublic = p === '/' || PUBLIC_DARK_PREFIXES.some((x) => p === x.replace(/\/$/, '') || p.startsWith(x.endsWith('/') ? x : x + '/'));
    if (isPublic) {
      document.documentElement.classList.add('dark');
    } else if (localStorage.getItem('veluro_theme') === 'light') {
      document.documentElement.classList.remove('dark');
    } else {
      document.documentElement.classList.add('dark');
    }
  }, [location.pathname]);
  return null;
}

function App() {
  useEffect(() => {
    const s = document.getElementById('app-splash');
    if (s) { s.style.opacity = '0'; setTimeout(() => s.remove(), 500); }
    startVersionWatch();
  }, []);
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <ThemeEnforcer />
      <AuthProvider>
        <BusinessProvider>
        <AppRouter />
        <GuidedTourBar />
        </BusinessProvider>
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            style: {
              background: 'rgba(24, 24, 27, 0.9)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fafafa',
              backdropFilter: 'blur(12px)',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
